/**
 * Predictive search overlay controller (#42).
 *
 * Listens for the `header:search-toggle` CustomEvent dispatched by
 * `sections/header.liquid` (lines 1158-1170) and toggles the overlay shell
 * rendered by `snippets/search-overlay.liquid`. While open, typing into
 * `[data-search-overlay-input]` fires a single multi-index `client.search`
 * call (debounced 200ms) that mirrors the headless
 * `tools/hooks/useMultiIndexSearch.ts` contract:
 *   - <prefix>_products                    (8 hits, facets: ['*'])
 *   - <prefix>_collections                 (4 hits)
 *   - <prefix>_products_query_suggestions  (4 hits)
 *
 * Renders results into the three named slots inside
 * `[data-search-overlay-results]` and announces the consolidated result
 * count through the polite live region.
 *
 * The Algolia client is built via the shared helper at
 * `theme/assets/algolia-client.js` (#41). We deliberately do NOT pull in
 * `@algolia/autocomplete-js`:
 *
 *   - The headless storefront performs the same consolidated
 *     `client.search` itself (no autocomplete-js indirection), and
 *     CLAUDE.md §9 mandates the headless contract wins where the issue
 *     prose disagrees.
 *   - autocomplete-js's classic theme fights the brand tokens captured in
 *     `tokens.css`; the bespoke styles in `search-overlay.css` already
 *     match the six visual references in the issue body.
 *   - One less CDN bundle to pin/csp-allow.
 *
 * Accessibility contract (audit #14 / 2.1.1-03)
 * ---------------------------------------------
 *   - Root carries `hidden` + `inert` whenever the overlay is closed so
 *     the close button is removed from the document tab order.
 *   - Focus is moved to the input on open and restored to the trigger on
 *     close (the trigger element is cached on every toggle).
 *   - Tab is trapped between the input and the close button.
 *   - Escape, backdrop click, and the close button all dismiss.
 *   - `aria-live="polite"` region announces "{count} results found for
 *     {query}" after each settled search (matches the headless contract).
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 200;
  var POPULAR_LIMIT = 5;
  // Featured cap: matches headless `HeaderSearch/index.tsx`
  // `setResults(searchResults.products.hits.slice(0, 4))` -- the overlay
  // surfaces 4 hero products; everything else is reachable via the
  // "View N results" link at the bottom of the sidebar.
  var PRODUCTS_LIMIT = 4;

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function getAlgolia() {
    return window.RTLAlgolia || null;
  }

  function getTrigger() {
    return document.querySelector('[data-search-toggle]');
  }

  function getFocusable(container) {
    if (!container) return [];
    var selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.prototype.filter.call(
      container.querySelectorAll(selectors),
      function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      }
    );
  }

  function announce(liveEl, count, query) {
    if (!liveEl) return;
    // Wipe → set so consecutive updates re-announce even if text matches.
    liveEl.textContent = '';
    if (!query) return;
    var t =
      window.theme && window.theme.translations && window.theme.translations.search_results_announcement;
    var text;
    if (typeof t === 'string') {
      text = t.replace('{{ count }}', count).replace('{{ query }}', query);
    } else {
      text = count + ' results found for ' + query;
    }
    // Defer one tick so screen readers pick the update up reliably.
    setTimeout(function () {
      liveEl.textContent = text;
    }, 50);
  }

  /**
   * Format a price the way the live AU storefront renders it inside the
   * search overlay's product cards: "$149 AUD" (symbol+integer when the
   * value is whole, with the ISO currency code as a trailing suffix). The
   * shared `algolia.formatPrice` returns the locale-default Intl format
   * which on en-AU prints "AUD$149.00" -- close but not matching live's
   * format. We only override the format inside the overlay; collection /
   * product templates that call the shared helper keep their behaviour.
   */
  function formatOverlayPrice(algolia, amount) {
    var cfg = algolia.getConfig();
    var numeric = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numeric)) return '';
    var symbol = (cfg && cfg.currency) || '$';
    var code = (cfg && cfg.currencyCode) || '';
    // Whole-number => no decimals (live shows "$149 AUD"). Fractional =>
    // two-decimal precision so "$24.95" stays readable.
    var formatted = numeric % 1 === 0 ? numeric.toFixed(0) : numeric.toFixed(2);
    return symbol + formatted + (code ? ' ' + code : '');
  }

  function tagHighlight(value, matchLevel) {
    // Algolia ships `_highlightResult.<field>.value` pre-wrapped with
    // `<em>` for matching ranges. We swap those to `<mark>` so they pick
    // up the design token treatment without changing Algolia's contract.
    if (!value) return '';
    return String(value).replace(/<em>/g, '<mark>').replace(/<\/em>/g, '</mark>');
  }

  function buildSuggestionItem(algolia, hit, query, onNavigate) {
    var li = document.createElement('li');
    li.className = 'search-overlay__suggestion';
    var a = document.createElement('a');
    a.className = 'search-overlay__suggestion-link';
    var cfg = algolia.getConfig();
    var q = hit && hit.query ? hit.query : '';
    a.href = cfg.searchUrl + '?q=' + encodeURIComponent(q);
    a.setAttribute('data-search-suggestion', '');
    if (hit && hit._highlightResult && hit._highlightResult.query) {
      a.innerHTML = tagHighlight(hit._highlightResult.query.value, hit._highlightResult.query.matchLevel);
    } else {
      a.textContent = q;
    }
    a.addEventListener('click', onNavigate);
    li.appendChild(a);
    return li;
  }

  function buildProductItem(algolia, hit, query, onNavigate) {
    var cfg = algolia.getConfig();
    var li = document.createElement('li');
    li.className = 'search-overlay__product';

    var a = document.createElement('a');
    a.className = 'search-overlay__product-link';
    a.href = algolia.hitProductUrl(hit);
    a.addEventListener('click', onNavigate);

    var media = document.createElement('span');
    media.className = 'search-overlay__product-media';
    var imgSrc = hit && hit.image ? hit.image : (hit && hit.images && hit.images[0]) || '';
    if (imgSrc) {
      var img = document.createElement('img');
      img.className = 'search-overlay__product-image';
      img.src = imgSrc;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
    }
    a.appendChild(media);

    var body = document.createElement('span');
    body.className = 'search-overlay__product-body';

    var title = document.createElement('span');
    title.className = 'search-overlay__product-title';
    if (hit && hit._highlightResult && hit._highlightResult.title) {
      title.innerHTML = tagHighlight(hit._highlightResult.title.value);
    } else {
      title.textContent = hit && hit.title ? hit.title : '';
    }
    body.appendChild(title);

    var priceWrap = document.createElement('span');
    priceWrap.className = 'search-overlay__product-prices';

    // QA round 1 items 1o / M1f: NOW price first; red when on sale.
    var onSale =
      hit &&
      typeof hit.compare_at_price === 'number' &&
      hit.compare_at_price > (hit.price || 0);

    if (hit && typeof hit.price !== 'undefined' && hit.price !== null) {
      var price = document.createElement('span');
      price.className =
        'search-overlay__product-price' +
        (onSale ? ' search-overlay__product-price--on-sale' : '');
      price.textContent = formatOverlayPrice(algolia, hit.price);
      priceWrap.appendChild(price);
    }

    if (onSale) {
      var compare = document.createElement('span');
      compare.className = 'search-overlay__product-compare';
      compare.textContent = formatOverlayPrice(algolia, hit.compare_at_price);
      priceWrap.appendChild(compare);
    }

    body.appendChild(priceWrap);
    a.appendChild(body);
    li.appendChild(a);
    return li;
  }

  function renderSlot(node, items) {
    if (!node) return;
    while (node.firstChild) node.removeChild(node.firstChild);
    for (var i = 0; i < items.length; i++) node.appendChild(items[i]);
  }

  function controller(root) {
    var input = root.querySelector('[data-search-overlay-input]');
    var form = root.querySelector('[data-search-overlay-form]');
    // The fallback form lives OUTSIDE `.search-overlay` so it doesn't
    // add a sibling/child the structural diff walks against the headless
    // reference. Look it up globally.
    var fallbackForm = document.querySelector('[data-search-overlay-fallback-form]');
    var fallbackQ = document.querySelector('[data-search-overlay-fallback-q]');
    var closeBtn = root.querySelector('[data-search-close]');
    var resultsRoot = root.querySelector('[data-search-overlay-results]');
    var noResults = root.querySelector('[data-search-overlay-no-results]');
    var noResultsHeading = root.querySelector('[data-search-overlay-no-results-heading]');
    // Most-popular-collections tile block inside the no-results region
    // (#214). Hidden until a query returns zero product hits.
    var popularCollections = root.querySelector('[data-search-popular-collections]');
    var liveEl = root.querySelector('[data-search-overlay-live]');
    // Sidebar / columns markup ships in a `<template>` sibling of root so
    // the static DOM tree matches the headless empty-state. We clone it
    // into `resultsRoot` on first non-empty hit, and prune on clear.
    var innerTemplate = document.querySelector('[data-search-overlay-results-inner-template]');
    var panel = null;  // unused placeholder for legacy focus-trap path
    var resultsInner = null;
    var sidebar = null;
    var popular = null;
    var popularHeading = null;
    var suggestionsList = null;
    var productsWrap = null;
    var productsList = null;
    var viewAll = null;

    function ensureResultsInner() {
      if (resultsInner) return resultsInner;
      if (!innerTemplate || !resultsRoot) return null;
      var frag = innerTemplate.content.cloneNode(true);
      resultsRoot.appendChild(frag);
      resultsInner = resultsRoot.querySelector('[data-search-overlay-results-inner]');
      sidebar = resultsRoot.querySelector('[data-search-overlay-sidebar]');
      popular = resultsRoot.querySelector('[data-search-overlay-popular]');
      popularHeading = resultsRoot.querySelector('[data-search-overlay-popular-heading]');
      suggestionsList = resultsRoot.querySelector('[data-source="querySuggestions"]');
      productsWrap = resultsRoot.querySelector('[data-search-overlay-products-wrap]');
      productsList = resultsRoot.querySelector('[data-source="products"]');
      viewAll = resultsRoot.querySelector('[data-search-overlay-view-all]');
      return resultsInner;
    }

    function removeResultsInner() {
      if (!resultsInner) return;
      if (resultsInner.parentNode) resultsInner.parentNode.removeChild(resultsInner);
      resultsInner = null;
      sidebar = null;
      popular = null;
      popularHeading = null;
      suggestionsList = null;
      productsWrap = null;
      productsList = null;
      viewAll = null;
    }

    var lastTrigger = null;
    var bodyOverflowPrev = '';
    var client = null;

    /* Resolve the bottom edge of the *visible* sticky header band, in
       viewport coordinates. The overlay docks flush against this so the
       search bar slab sits directly beneath the painted header at any
       scroll position -- mirroring the headless
       `top: calc(var(--header-height) + var(--header-top))` where
       `--header-top` is scroll-aware and collapses to 0 once the
       announcement bar scrolls out of view.

       Why a live measure rather than the CSS calc fallback
       (`--site-header-height + --announcement-bar-height`): the published
       `--announcement-bar-height` is the announcement bar's static
       `offsetHeight` (~46px) and never decrements on scroll, while the
       announcement bar is `position: relative` and scrolls away with the
       page. Once scrolled past it, that 46px becomes a phantom gap that
       detaches the overlay from the resurfaced sticky header. Reading the
       header's own `getBoundingClientRect().bottom` is naturally
       scroll-aware -- the announcement bar's contribution is already baked
       into the header's on-screen position.

       Element choice:
       - Transparent-capable header (home): the outer `.site-header` is
         0-height; the painted bar lives in `.site-header__inner`
         (absolute-positioned). Prefer the inner so we dock below the
         actual painted bar.
       - Non-transparent header (every other route): `.site-header`
         (`[data-site-header]`) IS the painted, sticky bar -- it carries
         the `is-hidden` (`translateY`) / `is-scrolled` state, so its rect
         bottom tracks the visible band even mid hide/resurface. The
         `.shopify-section--header` section wrapper is the sticky
         positioning context (pinned at top:0) and stays put while the bar
         translates; use it only as a fallback when the painted bar is
         off-screen (`bottom <= 0`, i.e. fully hidden). Never repurpose the
         `.site-header__inner` content row here -- it is the ~24px row
         inside the bar's padding and would dock the overlay ~20px above
         the bar's true bottom. */
    function headerBottom() {
      var transparentHeader = document.querySelector('.site-header--transparent-capable');
      if (transparentHeader) {
        var innerEl = transparentHeader.querySelector('.site-header__inner') || transparentHeader;
        return innerEl.getBoundingClientRect().bottom;
      }
      var bar = document.querySelector('[data-site-header], header.site-header');
      if (bar) {
        var barBottom = bar.getBoundingClientRect().bottom;
        if (barBottom > 0) return barBottom;
      }
      var wrapper = document.querySelector('section.shopify-section-group-header-group');
      if (wrapper) {
        var wrapBottom = wrapper.getBoundingClientRect().bottom;
        if (wrapBottom > 0) return wrapBottom;
      }
      return 0;
    }

    /* Dock the overlay top against the visible header bottom. JS-set inline
       `top` wins over the CSS calc fallback in `search-overlay.css`. Called
       on open, on a post-open animation frame (the header may still be
       settling its `is-scrolled` / `is-hidden` classes when search is
       opened mid-scroll), and on `resize` while open. */
    function reposition() {
      if (root.hasAttribute('hidden')) return;
      var bottom = headerBottom();
      if (bottom > 0) {
        root.style.top = Math.round(bottom) + 'px';
      }
      var resultsEl = root.querySelector('[data-search-overlay-results]');
      if (resultsEl) {
        var avail = window.innerHeight - resultsEl.getBoundingClientRect().top;
        if (avail > 0) {
          root.style.setProperty('--search-overlay-results-max-height', Math.round(avail) + 'px');
        }
      }
    }

    function onResize() {
      reposition();
    }

    function ensureClient() {
      if (client) return client;
      var algolia = getAlgolia();
      if (!algolia) return null;
      client = algolia.createSearchClient();
      return client;
    }

    function setExpanded(open) {
      input.setAttribute('aria-expanded', open ? 'true' : 'false');
      var trigger = getTrigger();
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function lockScroll() {
      bodyOverflowPrev = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }

    function unlockScroll() {
      document.documentElement.style.overflow = bodyOverflowPrev;
    }

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      var focusable = getFocusable(root);
      if (!focusable.length) {
        e.preventDefault();
        input.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      trapFocus(e);
    }

    function open() {
      if (!root.hasAttribute('hidden')) return;
      lastTrigger = getTrigger() || document.activeElement;
      /* Dock the overlay below the visible sticky header band. See
         `headerBottom()` / `reposition()` above -- the offset is derived
         from the header's live `getBoundingClientRect().bottom`, which is
         scroll-aware, so the overlay stays flush at any scroll position
         (it does NOT statically reserve the announcement-bar height the
         way the CSS calc fallback does). */
      reposition();

      /* Defense-in-depth: close any open mega-menu before the overlay paints,
         so a hovered mega-menu panel (z:10 inside the header section z:100)
         can't keep stacking above the search overlay. The mega-menu's open
         state is `.mega-menu.is-visible` + `aria-expanded="true"` on the
         related nav trigger -- mirror the existing close handler in
         `header.liquid` by clearing the class and resetting trigger state. */
      var openMegaMenu = document.querySelector('.mega-menu.is-visible');
      if (openMegaMenu) {
        openMegaMenu.classList.remove('is-visible');
        var hoverIndicator = document.querySelector('.mega-menu__hover-indicator');
        if (hoverIndicator) hoverIndicator.classList.remove('is-visible');
        document.querySelectorAll('[data-mega-menu-trigger][aria-expanded="true"]').forEach(function (t) {
          t.setAttribute('aria-expanded', 'false');
        });
      }
      root.removeAttribute('hidden');
      root.removeAttribute('inert');
      root.classList.add('is-open');
      lockScroll();
      setExpanded(true);
      // Defer so the browser applies the removed `hidden` before we move focus.
      // Empty-state contract: do NOT pre-render Popular Searches. The headless
      // `HeaderSearch/index.tsx` gates `.resultsContainer.visible` on
      // `isOpen && searchTerm` -- the results panel only appears once the
      // user has typed something. Matches the live AU storefront visual
      // contract (open-empty state shows just the white search bar slab).
      window.requestAnimationFrame(function () {
        input.focus();
        if (input.value) runSearch(input.value);
        /* Re-measure once the header has settled. If search is opened
           mid-scroll the header may still be flipping its
           `is-scrolled` / `is-hidden` classes (the section controller's
           own rAF-throttled scroll handler), so the open-time measure can
           be a frame stale. This second pass docks against the resting
           header bottom. */
        reposition();
      });
      window.addEventListener('resize', onResize, { passive: true });
      document.addEventListener('keydown', onKeyDown);
    }

    function close() {
      if (root.hasAttribute('hidden')) return;
      root.setAttribute('hidden', '');
      root.setAttribute('inert', '');
      root.classList.remove('is-open');
      root.style.top = '';
      unlockScroll();
      setExpanded(false);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKeyDown);
      input.value = '';
      clearResults();
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus();
      }
    }

    function clearResults() {
      removeResultsInner();
      if (noResults) noResults.classList.remove('is-hidden');
      if (noResultsHeading) noResultsHeading.textContent = '';
      // The popular-collections block only shows on a no-results-with-query
      // state; reset it to hidden whenever results are cleared.
      if (popularCollections) popularCollections.hidden = true;
      if (resultsRoot) {
        resultsRoot.classList.remove('is-visible');
      }
      if (liveEl) liveEl.textContent = '';
    }

    function syncSidebarVisibility() {
      // The sidebar `<aside>` is the container for two independent slots:
      // the Popular Searches block and the "View N results" CTA. Show it
      // whenever EITHER slot has visible content. The Popular wrapper is
      // always laid out (matches live's empty `.popularSearches` div),
      // so "has popular content" means "the heading is visible" --
      // equivalent to "QS index returned hits".
      if (!sidebar) return;
      var popularHasContent = popularHeading && !popularHeading.hidden;
      var viewAllVisible = viewAll && !viewAll.hidden;
      sidebar.hidden = !(popularHasContent || viewAllVisible);
    }

    function onNavigate() {
      // Allow the browser to navigate; close the overlay afterwards so
      // the focus return on close doesn't fight the page navigation.
      window.setTimeout(close, 0);
    }

    function setViewAllLabel(total, query) {
      if (!viewAll) return;
      var algolia = getAlgolia();
      var cfg = algolia ? algolia.getConfig() : { searchUrl: '/search' };
      viewAll.href = cfg.searchUrl + '?q=' + encodeURIComponent(query || '');
      var template =
        window.theme && window.theme.translations && window.theme.translations.search_view_all;
      var text;
      if (typeof template === 'string') {
        text = template.replace('{{ count }}', total).replace('{{ query }}', query || '');
      } else if (query) {
        text = 'View ' + total + ' results for ' + query;
      } else {
        text = 'View all results';
      }
      viewAll.textContent = text;
    }

    function renderPopular(hits) {
      var algolia = getAlgolia();
      if (!algolia || !suggestionsList) return;
      if (!hits || !hits.length) {
        renderSlot(suggestionsList, []);
        // Hide the heading but keep the wrapper laid out as an empty flex
        // item. Live's empty `.popularSearches` div is present in the DOM
        // with zero children -- that's how `justify-content: space-between`
        // on the sidebar still pushes the View All CTA to the bottom edge.
        if (popularHeading) popularHeading.hidden = true;
        syncSidebarVisibility();
        return;
      }
      var items = hits.slice(0, POPULAR_LIMIT).map(function (hit) {
        return buildSuggestionItem(algolia, hit, '', onNavigate);
      });
      renderSlot(suggestionsList, items);
      if (popularHeading) popularHeading.hidden = false;
      syncSidebarVisibility();
    }

    function maybeRenderPopular() {
      // Pristine state: empty input. Fire a placeholder query so the
      // query-suggestions index can populate the sidebar if rows exist.
      var algolia = getAlgolia();
      if (!algolia) return;
      var c = ensureClient();
      if (!c) return;
      var cfg = algolia.getConfig();
      c.search({
        requests: [
          { indexName: cfg.suggestionsIndex, query: '', hitsPerPage: POPULAR_LIMIT },
        ],
      })
        .then(function (response) {
          var results = (response && response.results) || [];
          var qs = results[0] || {};
          var hits = qs.hits || [];
          if (resultsRoot) resultsRoot.hidden = !hits.length;
          renderPopular(hits);
        })
        .catch(function () {
          /* swallow -- sidebar simply stays hidden */
        });
    }

    function applyResults(query, results) {
      var algolia = getAlgolia();
      if (!algolia) return;
      var products = results[0] || {};
      // #250: Collections column removed from the overlay to match the live AU
      // storefront (product suggestions only). Query is now [products, suggestions].
      var suggestions = results[1] || {};

      var productsHits = products.hits || [];
      var suggestionsHits = suggestions.hits || [];

      var productsCount = typeof products.nbHits === 'number' ? products.nbHits : productsHits.length;
      // The "View N results" CTA shows the products-index count only --
      // matches the live AU storefront ("View 2859 results for dress" on
      // 2026-05-21 == products.nbHits). Collections hits drive a separate
      // surface (the headless storefront's search results page tabs) but
      // do not contribute to the overlay's visible count.
      var total = productsCount;

      // Materialise the sidebar / columns grid from the `<template>` if
      // there are hits to show. This MUST happen before the slot-targeted
      // helpers below (renderPopular, setViewAllLabel, etc.) so they can
      // bind to the cloned DOM nodes.
      if (total > 0) ensureResultsInner();

      // Sidebar -- query suggestions.
      renderPopular(suggestionsHits);

      // Sidebar -- "View N results for q" CTA. Independent of popular-hits;
      // shown whenever there's any product/collection hit so users always
      // have a visible path to the full results page (B1 / review #4287203091).
      if (viewAll) {
        if (total > 0) {
          setViewAllLabel(total, query);
          viewAll.hidden = false;
        } else {
          viewAll.hidden = true;
        }
      }
      syncSidebarVisibility();

      // Products column.
      if (productsList && productsWrap) {
        var productItems = productsHits.slice(0, PRODUCTS_LIMIT).map(function (hit) {
          return buildProductItem(algolia, hit, query, onNavigate);
        });
        renderSlot(productsList, productItems);
        productsWrap.hidden = productItems.length === 0;
      }

      // Results root visibility -- toggle is-visible (opacity transition).
      // The structural diff sees the same layout boxes the reference does;
      // the change is purely opacity + pointer-events.
      if (resultsRoot) {
        resultsRoot.classList.toggle('is-visible', !!query);
      }

      // No-results vs results-inner visibility (mutually exclusive when
      // query is active). When there are hits, hide the no-results
      // message. When there are no hits but a query, materialise the
      // no-results message and ensure the inner grid is pruned.
      if (total > 0) {
        if (noResults) noResults.classList.add('is-hidden');
      } else {
        removeResultsInner();
        if (noResults) noResults.classList.remove('is-hidden');
      }

      if (noResults && noResultsHeading) {
        if (total === 0 && query) {
          var noResultsTemplate =
            window.theme && window.theme.translations && window.theme.translations.search_no_results;
          if (typeof noResultsTemplate === 'string') {
            noResultsHeading.textContent = noResultsTemplate.replace('{{ query }}', query);
          } else {
            noResultsHeading.textContent = 'No results found for ' + query;
          }
        }
      }

      // Reveal the most-popular-collections tiles only in the
      // no-results-with-query state (#214 task 5). Any product hit, or an
      // empty query, keeps the block hidden.
      if (popularCollections) {
        popularCollections.hidden = !(total === 0 && query);
      }

      announce(liveEl, total, query);
    }

    var pendingToken = 0;
    function runSearch(query) {
      var algolia = getAlgolia();
      if (!algolia) return;
      var c = ensureClient();
      if (!c) return;
      var cfg = algolia.getConfig();
      var trimmed = (query || '').trim();

      if (!trimmed) {
        // Reset to pristine state: hide the results panel via opacity
        // (NOT display: none -- the headless `.resultsContainer` is
        // always laid out, just transparent when isOpen && !searchTerm).
        // Keeps the structural diff aligned with the reference.
        removeResultsInner();
        if (noResults) noResults.classList.remove('is-hidden');
        if (popularCollections) popularCollections.hidden = true;
        if (resultsRoot) resultsRoot.classList.remove('is-visible');
        return;
      }

      pendingToken += 1;
      var token = pendingToken;

      c.search({
        requests: [
          {
            indexName: cfg.productsIndex,
            query: trimmed,
            hitsPerPage: PRODUCTS_LIMIT,
            facets: ['*'],
          },
          {
            indexName: cfg.suggestionsIndex,
            query: trimmed,
            hitsPerPage: POPULAR_LIMIT,
          },
        ],
      })
        .then(function (response) {
          // Drop stale responses.
          if (token !== pendingToken) return;
          var results = (response && response.results) || [];
          applyResults(trimmed, results);
        })
        .catch(function () {
          if (token !== pendingToken) return;
          // Silent failure -- overlay just shows nothing rather than blow up.
        });
    }

    var debouncedSearch = debounce(runSearch, DEBOUNCE_MS);

    // Wire DOM.
    input.addEventListener('input', function (e) {
      debouncedSearch(e.target.value);
    });

    // Enter key inside the visible input -- mirror the value to the
    // hidden fallback form and submit. Lets the no-JS / pre-hydration
    // path 302 to /search?q=... too via the same form action.
    input.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var trimmed = (input.value || '').trim();
      if (!trimmed) {
        e.preventDefault();
        return;
      }
      if (fallbackForm && fallbackQ) {
        fallbackQ.value = trimmed;
        fallbackForm.submit();
        return;
      }
      // Last-resort fallback: direct navigation.
      var algolia = getAlgolia();
      var cfg = algolia ? algolia.getConfig() : { searchUrl: '/search' };
      window.location.href = cfg.searchUrl + '?q=' + encodeURIComponent(trimmed);
    });

    if (closeBtn) closeBtn.addEventListener('click', close);

    // Backdrop click -- if the user clicks on the translucent wash area
    // of `.search-overlay` (the root), but NOT on any nested interactive
    // element, dismiss. Mirrors the headless React onClick gate
    // (`if (e.currentTarget === e.target)`).
    root.addEventListener('click', function (e) {
      if (e.target === root) close();
    });

    // Listen for the header dispatcher. The header only emits a toggle
    // event (no payload), so treat each dispatch as a flip.
    document.addEventListener('header:search-toggle', function () {
      if (root.hasAttribute('hidden')) open();
      else close();
    });

    // Defensive: also listen for the issue-prose name in case any future
    // consumer publishes it (it costs nothing and prevents a regression).
    document.addEventListener('search:open', open);
    document.addEventListener('search:close', close);
  }

  ready(function () {
    var root = document.querySelector('[data-search-overlay]');
    if (!root) return;
    // Wait until the algolia helper is on the page. The shared client
    // loads via a `defer`-tagged script in `theme.liquid`; it should be
    // ready by DOMContentLoaded, but poll a few ticks to be safe.
    var attempts = 0;
    (function tick() {
      if (window.RTLAlgolia) {
        controller(root);
        return;
      }
      if (attempts++ > 50) return; // ~5s ceiling; give up silently
      window.setTimeout(tick, 100);
    })();
  });
})();
